

// |--------------------------------------------------------------------------
// | KEEN DATASET SETTINGS
// |--------------------------------------------------------------------------

import {indexByTimezone} from '@/analytics/settings/keen-settings'
import {getInterval, buildFilters} from '@/analytics/utils/keen-utils'

// CACHED DATASET NAME FORMAT:
// <SOURCE>-<INDEX_BY>-<GROUP_BY>-<STREAM>-<TIMEINTERVAL>_analyses

// List of settings for all Keen Cached Datasets
export const datasetOptions = {

  // |--------------------------------------------------------------------------
  // | GENERAL
  // |--------------------------------------------------------------------------

  networkArticleDownloadsAnalyses: (stream = 'article_downloads', timeframe = 'this_60_days') => ({
    display_name: 'Web article downloads analyses: count events, count unique users & articles indexed by timezone_offset (network)',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
      'count_events_unique_article': {
        analysis_type: 'count_unique',
        target_property: 'custom_attributes.alertable_id',
      },
    },
    filters: buildFilters(),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'time.utc.timezone_offset',
    timezone: 'UTC',
  }),

  networkArticleArticleDownloadsAnalyses: (stream = 'article_downloads', timeframe = 'this_60_days') => ({
    display_name: 'Web article downloads analyses: count events, count unique users & articles, group_by article_id indexed by timezone_offset (network)',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
      'count_events_unique_article': {
        analysis_type: 'count_unique',
        target_property: 'custom_attributes.alertable_id',
      },
    },
    filters: buildFilters(),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'time.utc.timezone_offset',
    group_by: 'custom_attributes.alertable_id',
    timezone: 'UTC',
  }),

  channelArticleDownloadsAnalyses: (stream = 'article_downloads', timeframe = 'this_60_days') => ({
    display_name: 'Web article downloads analyses: count events, count unique users & articles indexed by channel_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
      'count_events_unique_article': {
        analysis_type: 'count_unique',
        target_property: 'custom_attributes.alertable_id',
      },
    },
    filters: buildFilters(),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.channel_id',
    timezone: 'UTC',
  }),

  articleArticleDownloadsAnalyses: (stream = 'article_downloads', timeframe = 'this_60_days') => ({
    display_name: 'Web article downloads analyses: count events, count unique users',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters(),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.alertable_id',
    timezone: 'UTC',
  }),

  channelArticleArticleDownloadsAnalyses: (stream = 'article_downloads', timeframe = 'this_60_days') => ({
    display_name: 'Web article downloads analyses: count events, count unique users & articles grouped_by alertable, indexed by channel_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters(),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.channel_id',
    group_by: 'custom_attributes.alertable_id',
    timezone: 'UTC',
  }),

  channelArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: count events indexed by channel_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
      'count_events_unique_article': {
        analysis_type: 'count_unique',
        target_property: 'custom_attributes.alertable_id',
      },
      'count_events_unique_analyst': {
        analysis_type: 'count_unique',
        target_property: 'custom_attributes.primary_analyst_id',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters(),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.channel_id',
    timezone: 'UTC',
  }),

  channelReadCategoryArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: count events, grouped by read category, indexed by channel_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
      'count_events_unique_article': {
        analysis_type: 'count_unique',
        target_property: 'custom_attributes.alertable_id',
      },
      'count_events_unique_analyst': {
        analysis_type: 'count_unique',
        target_property: 'custom_attributes.primary_analyst_id',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters(),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.channel_id',
    group_by: 'custom_attributes.read_category',
    timezone: 'UTC',
  }),

  networkArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: count events indexed by timezone_offset (network)',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
      'count_events_unique_article': {
        analysis_type: 'count_unique',
        target_property: 'custom_attributes.alertable_id',
      },
      'count_events_unique_analyst': {
        analysis_type: 'count_unique',
        target_property: 'custom_attributes.primary_analyst_id',
      },
      'count_events_unique_account': {
        analysis_type: 'count_unique',
        target_property: 'custom_attributes.account_id',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('network'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: indexByTimezone[stream],
    timezone: 'UTC',
  }),

  networkReadCategoryArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: count events, grouped by read category, indexed by timezone_offset (network)',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
      'count_events_unique_article': {
        analysis_type: 'count_unique',
        target_property: 'custom_attributes.alertable_id',
      },
      'count_events_unique_analyst': {
        analysis_type: 'count_unique',
        target_property: 'custom_attributes.primary_analyst_id',
      },
      'count_events_unique_account': {
        analysis_type: 'count_unique',
        target_property: 'custom_attributes.account_id',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('network'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: indexByTimezone[stream],
    group_by: 'custom_attributes.read_category',
    timezone: 'UTC',
  }),

  /*
  |--------------------------------------------------------------------------
  | INDEX_BY: USER
  |--------------------------------------------------------------------------
  */

  userArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: count events indexed by user_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_article': {
        analysis_type: 'count',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('user'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.user_id',
    timezone: 'UTC',
  }),


  userArticleArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: count events, grouped by article,  indexed by user_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('user'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.user_id',
    group_by: 'custom_attributes.alertable_id',
    timezone: 'UTC',
  }),

  userReadCategoryArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: count events, grouped by read category, indexed by user_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_article': {
        analysis_type: 'count_unique',
        target_property: 'custom_attributes.alertable_id',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('user'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.user_id',
    group_by: 'custom_attributes.read_category',
    timezone: 'UTC',
  }),

  channelAndUserArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: count events indexed by channel and user id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_article': {
        analysis_type: 'count_unique',
        target_property: 'custom_attributes.alertable_id',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('user'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: ['custom_attributes.channel_id', 'custom_attributes.user_id'],
    timezone: 'UTC',
  }),

  channelAndUserReadCategoryArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: count events, grouped by read category, indexed by channel and user id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_article': {
        analysis_type: 'count_unique',
        target_property: 'custom_attributes.alertable_id',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('user'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: ['custom_attributes.channel_id', 'custom_attributes.user_id'],
    group_by: 'custom_attributes.read_category',
    timezone: 'UTC',
  }),

  channelAndUserArticleArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: count events, grouped by article, indexed by channel and user id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('user'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: ['custom_attributes.channel_id', 'custom_attributes.user_id'],
    group_by: 'custom_attributes.alertable_id',
    timezone: 'UTC',
  }),

  /*
  |--------------------------------------------------------------------------
  | INDEX_BY: ACCOUNT
  |--------------------------------------------------------------------------
  */

  accountArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: count events indexed by account_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      count_events: {
        analysis_type: 'count',
      },
      count_events_unique_user: {
        analysis_type: 'count',
      }
    },
    filters: buildFilters('account'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.account_id',
    timezone: 'UTC',
  }),


  accountReadCategoryArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: count events, grouped by read category indexed by account_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('account'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.account_id',
    group_by: 'custom_attributes.read_category',
    timezone: 'UTC',
  }),

  accountArticleArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: count events, grouped by article,  indexed by account_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('account'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.account_id',
    group_by: 'custom_attributes.alertable_id',
    timezone: 'UTC',
  }),

  accountArticleArticleDownloadsAnalyses: (stream = 'article_downloads', timeframe = 'this_60_days') => ({
    display_name: 'Web article downloads analyses: count events, count unique users grouped by alertable_id, indexed by account_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('account'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.account_id',
    group_by: 'custom_attributes.alertable_id',
    timezone: 'UTC',
  }),

  accountUserSendgridAnalyses: (stream, timeframe = 'this_60_days') => ({
/**
 *     display_name: 'Sendgrid analyses: count unique by notifiable_id (user), indexed by alertable_id (newsletter)',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('sendgrid-newsletter'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.alertable_id',
    group_by: 'custom_attributes.notifiable_id',
    timezone: 'UTC',
 */

    display_name: 'Article email views analyses: sum views indexed by account_id, grouped by user_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('account'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.account_id',
    group_by: 'custom_attributes.notifiable_id',
    timezone: 'UTC',
  }),

  accountUserArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: sum read seconds indexed by account_id, grouped by user_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('account'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.account_id',
    group_by: 'custom_attributes.user_id',
    timezone: 'UTC',
  }),

  accountSendgridAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Sendgrid analyses: count by account_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('sendgrid-account-article'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.account_id',
    group_by: 'custom_attributes.alertable_id',
    timezone: 'UTC',
  }),

  accountArticleSendgridAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Sendgrid analyses: count unique by notifiable_id (user), indexed by account_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('sendgrid-account-article'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.account_id',
    group_by: 'custom_attributes.alertable_id',
    timezone: 'UTC',
  }),

  // |--------------------------------------------------------------------------
  // | INDEX_BY: PARTNER & ACCOUNT
  // |--------------------------------------------------------------------------

  channelAndAccountArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: count events indexed by channel and account_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('account'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: ['custom_attributes.channel_id', 'custom_attributes.account_id'],
    timezone: 'UTC',
  }),

  channelAndAccountReadCategoryArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: count events, grouped by read category, indexed by channel and account_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('account'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: ['custom_attributes.channel_id', 'custom_attributes.account_id'],
    group_by: 'custom_attributes.read_category',
    timezone: 'UTC',
  }),

  channelAndAccountArticleArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: count events, grouped by article,  indexed by account_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('account'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: ['custom_attributes.channel_id', 'custom_attributes.account_id'],
    group_by: 'custom_attributes.alertable_id',
    timezone: 'UTC',
  }),

  channelAndAccountArticleArticleDownloadsAnalyses: (stream = 'article_downloads', timeframe = 'this_60_days') => ({
    display_name: 'Web article downloads analyses: count events, count unique users grouped by alertable_id, indexed by account_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('account'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: ['custom_attributes.channel_id', 'custom_attributes.account_id'],
    group_by: 'custom_attributes.alertable_id',
    timezone: 'UTC',
  }),


  channelAndAccountUserArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: sum read seconds indexed by account_id, grouped by user_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('account'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: ['custom_attributes.channel_id', 'custom_attributes.account_id'],
    group_by: 'custom_attributes.user_id',
    timezone: 'UTC',
  }),

  /*
  |--------------------------------------------------------------------------
  | INDEX_BY: ANALYST
  |--------------------------------------------------------------------------
  */

  analystSendgridAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: count events indexed by primary_analyst_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_article': {
        analysis_type: 'count_unique',
        target_property: 'custom_attributes.alertable_id',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('analyst'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.primary_analyst_id',
    timezone: 'UTC',
  }),

  analystArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: count events indexed by primary_analyst_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_article': {
        analysis_type: 'count_unique',
        target_property: 'custom_attributes.alertable_id',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('analyst'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.primary_analyst_id',
    timezone: 'UTC',
  }),

  analystReadCategoryArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: count events, grouped by read category, indexed by primary_analyst_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_article': {
        analysis_type: 'count_unique',
        target_property: 'custom_attributes.alertable_id',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('analyst'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.primary_analyst_id',
    group_by: 'custom_attributes.read_category',
    timezone: 'UTC',
  }),

  analystArticleArticleDownloadsAnalyses: (stream = 'article_downloads', timeframe = 'this_60_days') => ({
    display_name: 'Web article downloads analyses: count events, count unique users grouped by alertable_id, indexed by primary_analyst_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('analyst'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.primary_analyst_id',
    group_by: 'custom_attributes.alertable_id',
    timezone: 'UTC',
  }),

  analystArticleArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: count events grouped by alertable_id, indexed by primary_analyst_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('analyst'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.primary_analyst_id',
    group_by: 'custom_attributes.alertable_id',
    timezone: 'UTC',
  }),

  analystAccountArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: count events grouped by account_id, indexed by primary_analyst_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('analyst'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.primary_analyst_id',
    group_by: 'custom_attributes.account_id',
    timezone: 'UTC',
  }),

  analystUserArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: count events grouped by user_id, indexed by primary_analyst_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'sum_read_seconds': {
        analysis_type: 'count_unique',
        target_property: 'custom_attributes.alertable_id',
      },
    },
    filters: buildFilters('analyst'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.primary_analyst_id',
    group_by: 'custom_attributes.user_id',
    timezone: 'UTC',
  }),

  /*
  |--------------------------------------------------------------------------
  | INDEX_BY: ARTICLE
  |--------------------------------------------------------------------------
  */

  articleArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: count, count unique by user, sum read seconds, avg scroll depth by article_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },
      'avg_max_scroll_depth': {
        analysis_type: 'average',
        target_property: 'custom_attributes.scroll_depth.ratio_max',
      },
    },
    filters: buildFilters(),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.alertable_id',
    timezone: 'UTC',
  }),

  articleReadCategoryArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: count, count unique by user, sum read seconds, avg scroll depth, grouped by read category, indexed by article_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },
      'avg_max_scroll_depth': {
        analysis_type: 'average',
        target_property: 'custom_attributes.scroll_depth.ratio_max',
      },
    },
    filters: buildFilters(),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.alertable_id',
    group_by: 'custom_attributes.read_category',
    timezone: 'UTC',
  }),

  // USE AS TEMPLATE FOR FUNCTION
  articleUserArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: sum read seconds indexed by alertable_id, grouped by user_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('article'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.alertable_id',
    group_by: 'custom_attributes.user_id',
    timezone: 'UTC',
  }),

  articleUserArticleDownloadsAnalyses: (stream = 'article_downloads', timeframe = 'this_60_days') => ({
    display_name: 'Web article downloads analyses: count events, count unique users & articles, indexed by alertable_id, grouped by user_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('article'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.alertable_id',
    group_by: 'custom_attributes.user_id',
    timezone: 'UTC',
  }),

  /*
  |--------------------------------------------------------------------------
  | INDEX_BY: NEWSLETTER
  |--------------------------------------------------------------------------
  */

  newsletterEmailNewsletterViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Newsletter views analyses: sum read seconds indexed by alertable_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('newsletter'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.alertable_id',
    timezone: 'UTC',
  }),

  newsletterReadCategoryEmailNewsletterViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Newsletter views analyses: sum read seconds, grouped by read category indexed by alertable_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('newsletter'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.alertable_id',
    group_by: 'custom_attributes.read_category',
    timezone: 'UTC',
  }),

  networkEmailNewsletterViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Newsletter views analyses: counts and sum read seconds indexed by network',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
      'count_events_unique_newsletter': {
        analysis_type: 'count_unique',
        target_property: 'custom_attributes.alertable_id',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },

    },
    filters: buildFilters('newsletter'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'time.timezone_offset',
    timezone: 'UTC',
  }),

  networkReadCategoryEmailNewsletterViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Newsletter views analyses: counts and sum read seconds, grouped by read category, indexed by network',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
      'count_events_unique_newsletter': {
        analysis_type: 'count_unique',
        target_property: 'custom_attributes.alertable_id',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },

    },
    filters: buildFilters('newsletter'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'time.timezone_offset',
    group_by: 'custom_attributes.read_category',
    timezone: 'UTC',
  }),

  newsletterUserEmailNewsletterViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Newsletter views analyses: sum read seconds groupbed_by user_id, indexed by alertable_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('newsletter'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.alertable_id',
    group_by: 'custom_attributes.user_id',
    timezone: 'UTC',
  }),


  /*
  |--------------------------------------------------------------------------
  | SENDGRID
  |--------------------------------------------------------------------------
  */

  userArticleSendgridAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Sendgrid analyses: count of events, grouped by article, indexed by notifiable_id (user)',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      }
    },
    filters: buildFilters('sendgrid-article'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.notifiable_id',
    group_by: 'custom_attributes.alertable_id',
    timezone: 'UTC',
  }),

  userSendgridAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Sendgrid analyses: count of events, indexed by notifiable_id (user)',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      }
    },
    filters: buildFilters('sendgrid-article'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.notifiable_id',
    timezone: 'UTC',
  }),

  articleSendgridAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Sendgrid analyses: count unique by notifiable_id (user), indexed by alertable_id (article)',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('sendgrid-article'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.alertable_id',
    timezone: 'UTC',
  }),

  alertSendgridAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Sendgrid analyses: count and count unique by notifiable_id (user), indexed by alert_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('sendgrid-alert'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.alert_id',
    timezone: 'UTC',
  }),

  articleUserSendgridAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Sendgrid analyses: count, grouped_by user indexed by alertable_id (article)',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('sendgrid-article'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.alertable_id',
    group_by: 'custom_attributes.notifiable_id',
    timezone: 'UTC',
  }),

  analystArticleSendgridAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Sendgrid analyses: count unique by notifiable_id (user), indexed by primary_analyst_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('sendgrid-article'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    group_by: 'custom_attributes.alertable_id',
    index_by: 'custom_attributes.primary_analyst_id',
    timezone: 'UTC',
  }),

  channelArticleTypeSendgridAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Sendgrid analyses: count unique by notifiable_id (user) & alertable_id, indexed by channel_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
      'count_events_unique_article': {
        analysis_type: 'count_unique',
        target_property: 'custom_attributes.alertable_id',
      },
    },
    filters: buildFilters('sendgrid-article'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.channel_id',
    timezone: 'UTC',
  }),

  channelArticleSendgridAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Sendgrid analyses: count unique by notifiable_id (user) & alertable_id, groupbed_by alertable, indexed by channel_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('sendgrid-article'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.channel_id',
    group_by: 'custom_attributes.alertable_id',
    timezone: 'UTC',
  }),

  networkArticleSendgridAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Sendgrid analyses: count unique by notifiable_id (user) & alertable_id, groupbed_by alertable, indexed by network',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('sendgrid-article'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'timestamp_info.timezone_offset',
    group_by: 'custom_attributes.alertable_id',
    timezone: 'UTC',
  }),

  networkNewsletterTypeSendgridAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Sendgrid analyses: count unique by notifiable_id (user) & alertable_id, indexed by network',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
      'count_events_unique_newsletter': {
        analysis_type: 'count_unique',
        target_property: 'custom_attributes.alertable_id',
      },
    },
    filters: buildFilters('sendgrid-newsletter'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'timestamp_info.timezone_offset',
    timezone: 'UTC',
  }),

  newsletterSendgridAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Sendgrid analyses: count unique by notifiable_id (user), indexed by alertable_id (newsletter)',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'count_events_unique_user': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('sendgrid-newsletter'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.alertable_id',
    timezone: 'UTC',
  }),

  newsletterUserSendgridAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Sendgrid analyses: count unique by notifiable_id (user), indexed by alertable_id (newsletter)',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('sendgrid-newsletter'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.alertable_id',
    group_by: 'custom_attributes.notifiable_id',
    timezone: 'UTC',
  }),

  /*
  |--------------------------------------------------------------------------
  | INDEX_BY: NETWORK
  |--------------------------------------------------------------------------
  */



  networkAccountSendgridAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Account view analyses: count of emails processed index by timezone_offset (network), grouped by account_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters(),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: indexByTimezone[stream],
    group_by: 'custom_attributes.account_id',
    timezone: 'UTC',
  }),


  networkAccountArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: sum read seconds, unique web views indexed by timezone_offset (network), grouped by account_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters(),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: indexByTimezone[stream],
    group_by: 'custom_attributes.account_id',
    timezone: 'UTC',
  }),

  networkAnalystArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: sum read seconds indexed by timezone_offset (network), grouped by primary_analyst_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('analyst'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: indexByTimezone[stream],
    group_by: 'custom_attributes.primary_analyst_id',
    timezone: 'UTC',
  }),

  networkArticleArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: sum read seconds indexed by timezone_offset (network), grouped by alertable_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters(),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: indexByTimezone[stream],
    group_by: 'custom_attributes.alertable_id',
    timezone: 'UTC',
  }),


  networkChannelArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: sum read seconds indexed by timezone_offset (network), grouped by channel_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('network'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: indexByTimezone[stream],
    group_by: 'custom_attributes.channel_id',
    timezone: 'UTC',
  }),

  networkCompanyArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: sum read seconds indexed by timezone_offset (network), grouped by company_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('company'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: indexByTimezone[stream],
    group_by: 'custom_attributes.company_id',
    timezone: 'UTC',
  }),

  networkGeoCountryArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: sum read seconds indexed by timezone_offset (network), grouped by geo.country',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('geo-country'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: indexByTimezone[stream],
    group_by: 'geo.country',
    timezone: 'UTC',
  }),

  networkUserArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: sum read seconds indexed by timezone_offset (network), grouped by user_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters(),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: indexByTimezone[stream],
    group_by: 'custom_attributes.alertable_id',
    timezone: 'UTC',
  }),

  networkUserSendgridAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'User view analyses: count of emails processed index by timezone_offset (network), grouped by user_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters(),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: indexByTimezone[stream],
    group_by: 'custom_attributes.user_id',
    timezone: 'UTC',
  }),

  networkSendgridAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Sendgrid analyses: count events indexed by timezone_offset (network)',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('sendgrid-network'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: indexByTimezone[stream],
    timezone: 'UTC',
  }),

  /*
  |--------------------------------------------------------------------------
  | INDEX_BY: PARTNER
  |--------------------------------------------------------------------------
  */

  channelAccountArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: sum read seconds indexed by channel_id, grouped by account_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters(),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.channel_id',
    group_by: 'custom_attributes.account_id',
    timezone: 'UTC',
  }),

  channelAnalystArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: sum read seconds indexed by channel, grouped by primary_analyst_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('analyst'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.channel_id',
    group_by: 'custom_attributes.primary_analyst_id',
    timezone: 'UTC',
  }),

  channelArticleArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: sum read seconds indexed by channel, grouped by alertable_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters(),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.channel_id',
    group_by: 'custom_attributes.alertable_id',
    timezone: 'UTC',
  }),

  channelCompanyArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: sum read seconds indexed by channel, grouped by company_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('company'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.channel_id',
    group_by: 'custom_attributes.company_id',
    timezone: 'UTC',
  }),

  channelGeoCountryArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: sum read seconds indexed by channel, grouped by geo.country',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('geo-country'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.channel_id',
    group_by: 'geo.country',
    timezone: 'UTC',
  }),

  channelUserArticleViewsAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Article views analyses: sum read seconds indexed by channel, grouped by user_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'sum_read_seconds': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters(),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.channel_id',
    group_by: 'custom_attributes.user_id',
    timezone: 'UTC',
  }),

  channelSendgridAnalyses: (stream, timeframe = 'this_60_days') => ({
    display_name: 'Sendgrid analyses: count events indexed by channel_id',
    event_collection: stream,
    analysis_type: 'multi_analysis',
    analyses: {
      'count_events': {
        analysis_type: 'count',
      },
    },
    filters: buildFilters('sendgrid-channel'),
    interval: getInterval(timeframe),
    timeframe: timeframe,
    index_by: 'custom_attributes.channel_id',
    timezone: 'UTC',
  }),
}
